import React, { FC, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { getExternalErrorMessage } from '@modernloop/shared/utils';
import { useSnackbar } from 'notistack';

import { AtsType, IntegrationType } from 'src/generated/mloop-graphql';

import {
  KomboIntegration,
  useIntegrationDelete,
  useKomboActivate,
  useKomboCreateLink,
  useKomboCreateReconnectLink,
} from 'src/hooks/api/integration';
import { getAtsService } from 'src/hooks/atsService';

import { DisconectAtsDialog } from '../Dialogs/DisconnectAtsDialog';
import KomboConnectDialog from '../Dialogs/KomboConnectDialog';
import IntegrationCard from '../IntegrationCard';

import { ConnectAtsButton } from './ConnectAtsButton';

const KULA_TOOL = 'kula';

interface ConnectFlow {
  link: string;
  reconnect: boolean;
}

export interface Props {
  kombo?: KomboIntegration;
  disabled?: boolean;
  onConnect?: () => void;
  onRemove?: () => void;
}

const KomboCard: FC<Props> = ({ kombo, disabled = false, onConnect, onRemove }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [openRemoveDialog, setOpenRemoveDialog] = useState<boolean>(false);
  const [connectFlow, setConnectFlow] = useState<ConnectFlow | null>(null);

  const createLink = useKomboCreateLink();
  const createReconnectLink = useKomboCreateReconnectLink();
  const activate = useKomboActivate();
  const deleteKombo = useIntegrationDelete(IntegrationType.Kombo);
  const atsService = getAtsService(AtsType.Kula);

  const isBusy = createLink.isLoading || createReconnectLink.isLoading || activate.isLoading || deleteKombo.isLoading;

  const openKomboConnect = async (reconnect: boolean) => {
    try {
      const { link } = reconnect
        ? await createReconnectLink.mutateAsync()
        : await createLink.mutateAsync({ integration_tool: KULA_TOOL });
      setConnectFlow({ link, reconnect });
    } catch (e) {
      enqueueSnackbar(getExternalErrorMessage(e), { variant: 'error' });
    }
  };

  const handleKomboConnectSuccess = async (activationToken: string) => {
    const reconnect = connectFlow?.reconnect ?? false;
    setConnectFlow(null);

    try {
      await activate.mutateAsync({ token: activationToken, reconnect });
      enqueueSnackbar(
        t(
          reconnect
            ? 'hiring-dashboard-settings-integrations:settings.integrations.kombo.toast.reconnected'
            : 'hiring-dashboard-settings-integrations:settings.integrations.kombo.toast.connected'
        ),
        { variant: 'success' }
      );
      onConnect?.();
    } catch (e) {
      enqueueSnackbar(getExternalErrorMessage(e), { variant: 'error' });
    }
  };

  return (
    <>
      <IntegrationCard
        dataTestId="kombo-card-integration-card"
        logo={atsService.logoUrl}
        name={atsService.name}
        onConnect={!kombo ? () => openKomboConnect(false) : undefined}
        onReconnect={kombo ? () => openKomboConnect(true) : undefined}
        onRemove={kombo ? () => setOpenRemoveDialog(true) : undefined}
        disabled={disabled || isBusy}
        connectButton={
          <ConnectAtsButton
            dataTestId="kombo-card-integration-card"
            handleConnect={() => openKomboConnect(false)}
            disabled={disabled || isBusy}
          />
        }
      />

      {connectFlow && (
        <KomboConnectDialog
          open
          url={connectFlow.link}
          onClose={() => setConnectFlow(null)}
          onSuccess={handleKomboConnectSuccess}
        />
      )}

      {openRemoveDialog && (
        <DisconectAtsDialog
          integrationName={atsService.name}
          onClose={() => setOpenRemoveDialog(false)}
          onRemove={() => {
            setOpenRemoveDialog(false);
            deleteKombo.mutate(undefined, {
              onSuccess: () => {
                onRemove?.();
              },
            });
          }}
        />
      )}
    </>
  );
};

export default KomboCard;
